/**
 * Files sidebar panel showing uploads and workspace files.
 * Combines upload progress and file list into unified collapsible sections.
 * Resizable via drag handle.
 *
 * Blueprint: specs/013-file-services/spec.md
 */

import React, { useEffect, useState, useCallback, useRef, useMemo, DragEvent } from 'react';
import {
  RefreshCw,
  FolderOpen,
  X,
  ChevronDown,
  ChevronRight,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Trash2,
  Plus,
  Search,
  Download,
  CheckSquare,
} from 'lucide-react';
import { FileItem } from './FileItem';
import { FileIcon } from './FileIcon';
import { useFileList, type FileInfo } from '../../hooks/useFileList';
import type { UploadState } from '../../hooks/useFileUpload';

const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

interface FilesSidebarProps {
  sessionId: string;
  userId: string;
  agentEndpoint?: string;
  uploads: UploadState[];
  onCancelUpload: (uploadId: string) => void;
  onCancelAllUploads: () => void;
  onClearCompletedUploads: () => void;
  isUploading: boolean;
  totalProgress: number;
  isOpen: boolean;
  onClose?: () => void;
  /** Key that triggers file list refresh when changed */
  refreshKey?: number;
  /** Callback to add files for upload */
  onAddFiles?: (files: FileList | File[]) => void;
  /** Shared file list from parent (for sync with autocomplete) */
  files?: FileInfo[];
  /** Shared refresh function from parent */
  onRefreshFiles?: () => void;
  /** Loading state from parent */
  filesLoading?: boolean;
  /** Error state from parent */
  filesError?: string | null;
}

type SortOption = 'recent' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc';

export function FilesSidebar({
  sessionId,
  userId,
  agentEndpoint = AGENT_URL,
  uploads,
  onCancelUpload,
  onCancelAllUploads,
  onClearCompletedUploads,
  isUploading,
  totalProgress,
  isOpen,
  onClose,
  refreshKey,
  onAddFiles,
  files: sharedFiles,
  onRefreshFiles,
  filesLoading,
  filesError,
}: FilesSidebarProps) {
  const [width, setWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const [uploadsCollapsed, setUploadsCollapsed] = useState(false);
  const [filesCollapsed, setFilesCollapsed] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('recent');
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const lastCompletedCountRef = useRef(0);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use shared file list if provided, otherwise use local hook
  const useSharedFiles = sharedFiles !== undefined;

  const {
    files: localFiles,
    loading: localLoading,
    error: localError,
    fetchFiles: localFetchFiles,
    deleteFile: deleteFileApi,
    deleteFiles,
    downloadFile,
    refreshFiles: localRefreshFiles,
  } = useFileList({
    sessionId,
    userId,
    agentEndpoint,
    autoFetch: !useSharedFiles && isOpen && !!sessionId && !!userId,
  });

  // Use shared or local state
  const files = useSharedFiles ? sharedFiles : localFiles;
  const loading = useSharedFiles ? (filesLoading ?? false) : localLoading;
  const error = useSharedFiles ? filesError : localError;
  const refreshFiles = useSharedFiles && onRefreshFiles ? onRefreshFiles : localRefreshFiles;
  const fetchFiles = useSharedFiles && onRefreshFiles ? onRefreshFiles : localFetchFiles;

  // Wrap deleteFile to also refresh the list after deletion
  const deleteFile = useCallback(
    async (path: string): Promise<boolean> => {
      const success = await deleteFileApi(path);
      if (success) {
        // Force refresh to ensure UI is in sync
        await refreshFiles();
      }
      return success;
    },
    [deleteFileApi, refreshFiles]
  );

  // Refresh file list when uploads complete
  useEffect(() => {
    const completedUploads = uploads.filter((u) => u.status === 'completed');
    if (completedUploads.length > 0) {
      refreshFiles();
    }
  }, [uploads, refreshFiles]);

  // Re-fetch when sidebar opens
  useEffect(() => {
    if (isOpen && sessionId && userId) {
      fetchFiles();
    }
  }, [isOpen, sessionId, userId, fetchFiles]);

  // Re-fetch when refreshKey changes (triggered by SSE file events)
  useEffect(() => {
    if (refreshKey && refreshKey > 0 && isOpen && sessionId && userId) {
      refreshFiles();
    }
  }, [refreshKey, isOpen, sessionId, userId, refreshFiles]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event: MediaQueryList | MediaQueryListEvent) => {
      setIsNarrow(event.matches);
    };
    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const completedCount = uploads.filter((u) => u.status === 'completed').length;
    if (completedCount > lastCompletedCountRef.current) {
      const diff = completedCount - lastCompletedCountRef.current;
      lastCompletedCountRef.current = completedCount;
      setUploadNotice(`Uploaded ${diff} file${diff === 1 ? '' : 's'} successfully`);
      const timer = window.setTimeout(() => {
        setUploadNotice(null);
      }, 3000);
      return () => window.clearTimeout(timer);
    }
    lastCompletedCountRef.current = completedCount;
    return undefined;
  }, [uploads]);

  // Resize handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isNarrow) return;
      e.preventDefault();
      setIsResizing(true);
    },
    [isNarrow],
  );

  useEffect(() => {
    if (!isResizing || isNarrow) {
      if (isResizing && isNarrow) {
        setIsResizing(false);
      }
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(240, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, isNarrow]);

  // File input change handler
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onAddFiles) {
        onAddFiles(files);
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onAddFiles]
  );

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0 && onAddFiles) {
        onAddFiles(files);
      }
    },
    [onAddFiles]
  );

  const handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // All hooks must be called before any early returns
  const activeUploads = useMemo(
    () => uploads.filter((u) => u.status === 'pending' || u.status === 'uploading'),
    [uploads]
  );
  const failedUploads = useMemo(
    () => uploads.filter((u) => u.status === 'failed'),
    [uploads]
  );
  const completedUploads = useMemo(
    () =>
      uploads.filter(
        (u) =>
          u.status === 'completed' ||
          u.status === 'failed' ||
          u.status === 'cancelled'
      ),
    [uploads]
  );

  const visibleFiles = useMemo(() => {
    const uploadFilenames = new Set(activeUploads.map((u) => u.filename));
    const filtered = files.filter((f) => !uploadFilenames.has(f.name));
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return filtered;
    return filtered.filter((file) => {
      const nameMatch = file.name.toLowerCase().includes(normalizedQuery);
      const pathMatch = file.path.toLowerCase().includes(normalizedQuery);
      return nameMatch || pathMatch;
    });
  }, [files, activeUploads, searchQuery]);

  const sortedFiles = useMemo(() => {
    const items = [...visibleFiles];
    const compareName = (a: FileInfo, b: FileInfo) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    switch (sortOption) {
      case 'name-asc':
        items.sort(compareName);
        break;
      case 'name-desc':
        items.sort((a, b) => compareName(b, a));
        break;
      case 'size-asc':
        items.sort((a, b) => a.size - b.size);
        break;
      case 'size-desc':
        items.sort((a, b) => b.size - a.size);
        break;
      default:
        items.sort(
          (a, b) =>
            new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
        );
    }
    return items;
  }, [visibleFiles, sortOption]);

  useEffect(() => {
    const visiblePathSet = new Set(visibleFiles.map((file) => file.path));
    setSelectedPaths((prev) => {
      const next = new Set(
        [...prev].filter((path) => visiblePathSet.has(path))
      );
      if (next.size === prev.size) {
        let unchanged = true;
        for (const path of next) {
          if (!prev.has(path)) {
            unchanged = false;
            break;
          }
        }
        if (unchanged) {
          return prev;
        }
      }
      return next;
    });
  }, [visibleFiles]);

  const toggleSelect = useCallback((path: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      const allSelected =
        sortedFiles.length > 0 &&
        sortedFiles.every((file) => prev.has(file.path));
      if (allSelected) {
        sortedFiles.forEach((file) => next.delete(file.path));
      } else {
        sortedFiles.forEach((file) => next.add(file.path));
      }
      return next;
    });
  }, [sortedFiles]);

  const clearSelection = useCallback(() => {
    setSelectedPaths(new Set());
  }, []);

  const handleBulkDownload = useCallback(async () => {
    const selectedFiles = sortedFiles.filter((file) =>
      selectedPaths.has(file.path)
    );
    for (const file of selectedFiles) {
      await downloadFile(file.path, file.name);
    }
  }, [sortedFiles, selectedPaths, downloadFile]);

  const handleBulkDelete = useCallback(async () => {
    const paths = Array.from(selectedPaths);
    if (!paths.length) return;
    await deleteFiles(paths);
    clearSelection();
    await refreshFiles();
  }, [selectedPaths, deleteFiles, refreshFiles, clearSelection]);

  // Early return after all hooks
  if (!isOpen) {
    return null;
  }

  const hasUploadsToShow = activeUploads.length > 0 || failedUploads.length > 0;
  const fileCount = visibleFiles.length;
  const selectedCount = selectedPaths.size;
  const allVisibleSelected =
    sortedFiles.length > 0 &&
    sortedFiles.every((file) => selectedPaths.has(file.path));

  return (
    <>
      {isNarrow && onClose && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={sidebarRef}
        className={`flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative ${
          isNarrow
            ? 'fixed inset-y-0 right-0 z-50 w-[90vw] max-w-sm shadow-2xl'
            : ''
        }`}
        style={isNarrow ? undefined : { width: `${width}px` }}
        data-testid="files-sidebar"
      >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="files-sidebar-file-input"
      />

      {/* Resize handle */}
      {!isNarrow && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
          onMouseDown={handleMouseDown}
          style={{ backgroundColor: isResizing ? '#38bdf8' : 'transparent' }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <FolderOpen size={18} className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">Files</span>
          {(fileCount > 0 || activeUploads.length > 0) && (
            <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
              {fileCount + activeUploads.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onAddFiles && (
            <button
              onClick={handleUploadButtonClick}
              className="p-1.5 text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded transition-colors"
              title="Upload files"
              data-testid="files-upload-button"
            >
              <Plus size={16} />
            </button>
          )}
          <button
            onClick={refreshFiles}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors disabled:opacity-50"
            title="Refresh files"
            data-testid="files-refresh-button"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title="Close sidebar"
              data-testid="files-sidebar-close"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {uploadNotice && (
        <div className="px-4 py-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-800 flex items-center gap-2">
          <CheckCircle size={14} className="text-emerald-600" />
          {uploadNotice}
        </div>
      )}

      {/* Content with drag-drop support */}
      <div
        className="flex-1 overflow-y-auto relative"
        onDragOver={onAddFiles ? handleDragOver : undefined}
        onDragLeave={onAddFiles ? handleDragLeave : undefined}
        onDrop={onAddFiles ? handleDrop : undefined}
      >
        {/* Drag-over overlay */}
        {isDragOver && onAddFiles && (
          <div
            className="absolute inset-0 bg-sky-50/90 dark:bg-sky-900/30 border-2 border-dashed border-sky-400 rounded-lg z-20 flex items-center justify-center"
            data-testid="files-drop-overlay"
          >
            <div className="text-center">
              <Upload className="w-10 h-10 text-sky-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-sky-700">Drop files to upload</p>
            </div>
          </div>
        )}

        {/* Uploads Section - show when there are active or failed uploads */}
        {hasUploadsToShow && (
          <div className="border-b border-gray-100 dark:border-gray-800">
            {/* Section Header */}
            <button
              onClick={() => setUploadsCollapsed(!uploadsCollapsed)}
              className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-2">
                {uploadsCollapsed ? (
                  <ChevronRight size={16} className="text-gray-400" />
                ) : (
                  <ChevronDown size={16} className="text-gray-400" />
                )}
                <Upload size={14} className={failedUploads.length > 0 ? "text-red-500" : "text-sky-500"} />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Uploads</span>
                {activeUploads.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 rounded-full">
                    {activeUploads.length}
                  </span>
                )}
                {failedUploads.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                    {failedUploads.length} failed
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isUploading && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Math.round(totalProgress)}%</span>
                )}
                {completedUploads.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearCompletedUploads();
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                    title="Clear completed"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </button>

            {/* Progress bar */}
            {isUploading && !uploadsCollapsed && (
              <div className="px-4 py-1 bg-gray-50 dark:bg-gray-800/50">
                <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sky-500 transition-all duration-300"
                    style={{ width: `${totalProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Upload items - active and failed uploads */}
            {!uploadsCollapsed && (
              <div className="py-1" data-testid="upload-queue">
                {/* Failed uploads first with prominent error display */}
                {failedUploads.map((upload) => (
                  <UploadItem
                    key={upload.id}
                    upload={upload}
                    onCancel={onCancelUpload}
                  />
                ))}
                {/* Active uploads */}
                {activeUploads.map((upload) => (
                  <UploadItem
                    key={upload.id}
                    upload={upload}
                    onCancel={onCancelUpload}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Files Section */}
        <div>
          {/* Section Header */}
          <button
            onClick={() => setFilesCollapsed(!filesCollapsed)}
            className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-2">
              {filesCollapsed ? (
                <ChevronRight size={16} className="text-gray-400" />
              ) : (
                <ChevronDown size={16} className="text-gray-400" />
              )}
              <FolderOpen size={14} className="text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Workspace Files</span>
              {fileCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                  {fileCount}
                </span>
              )}
            </div>
          </button>

          {/* File list */}
          {!filesCollapsed && (
            <div className="py-1">
              <div className="px-4 pt-3 pb-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search files..."
                      className="w-full pl-9 pr-8 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Clear search"
                      >
                        <X className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    )}
                  </div>
                  <select
                    value={sortOption}
                    onChange={(e) =>
                      setSortOption(e.target.value as SortOption)
                    }
                    className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-2 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-200"
                    title="Sort files"
                  >
                    <option value="recent">Recent</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="size-desc">Largest</option>
                    <option value="size-asc">Smallest</option>
                  </select>
                </div>

                {sortedFiles.length > 0 && (
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <button
                      onClick={toggleSelectAll}
                      className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-sky-600 dark:hover:text-sky-400"
                    >
                      <CheckSquare className="w-3.5 h-3.5" />
                      {allVisibleSelected ? 'Clear selection' : 'Select all'}
                    </button>
                    {selectedCount > 0 && (
                      <span>{selectedCount} selected</span>
                    )}
                  </div>
                )}

                {selectedCount > 0 && (
                  <div className="flex items-center justify-between rounded-lg border border-sky-100 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 px-2 py-1.5">
                    <span className="text-xs text-sky-700 dark:text-sky-400 font-medium">
                      {selectedCount} selected
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={handleBulkDownload}
                        className="p-1 text-sky-700 dark:text-sky-400 hover:text-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded"
                        title="Download selected"
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        className="p-1 text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                        title="Delete selected"
                      >
                        <Trash2 size={14} />
                      </button>
                      <button
                        onClick={clearSelection}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title="Clear selection"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : error ? (
                <div className="px-4 py-6 text-center">
                  <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
                  <button
                    onClick={refreshFiles}
                    className="mt-2 text-sm text-sky-600 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : sortedFiles.length === 0 ? (
                <div
                  className={`px-4 py-6 text-center ${onAddFiles ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''} text-gray-500 dark:text-gray-400`}
                  onClick={onAddFiles ? handleUploadButtonClick : undefined}
                  data-testid="files-empty-state"
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {searchQuery ? 'No files match your search' : 'No files yet'}
                  </p>
                  <p className="text-xs mt-1">
                    {searchQuery
                      ? 'Try a different keyword'
                      : onAddFiles
                        ? 'Click to upload or drag files here'
                        : 'Upload files using the attach button'}
                  </p>
                </div>
              ) : (
                sortedFiles.map((file) => (
                  <FileItem
                    key={file.path}
                    file={file}
                    onDownload={downloadFile}
                    onDelete={deleteFile}
                    selectable
                    selected={selectedPaths.has(file.path)}
                    onToggleSelect={toggleSelect}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

// Inline upload item component for unified view
interface UploadItemProps {
  upload: UploadState;
  onCancel: (uploadId: string) => void;
}

function UploadItem({ upload, onCancel }: UploadItemProps) {
  const getStatusIcon = () => {
    switch (upload.status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-500 flex-shrink-0" />;
      case 'failed':
        return <AlertCircle size={16} className="text-red-500 flex-shrink-0" />;
      case 'cancelled':
        return <XCircle size={16} className="text-gray-400 flex-shrink-0" />;
      case 'uploading':
        return <Loader2 size={16} className="text-sky-500 animate-spin flex-shrink-0" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />;
    }
  };

  const isActive = upload.status === 'uploading' || upload.status === 'pending';
  const isFailed = upload.status === 'failed';

  return (
    <div
      className={`group flex items-start gap-3 px-3 py-2 transition-colors ${
        isFailed ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
      data-testid={`upload-item-${upload.filename}`}
    >
      <FileIcon filename={upload.filename} className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isFailed ? 'text-red-900 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
            {upload.filename}
          </span>
          {getStatusIcon()}
        </div>
        {upload.status === 'uploading' && (
          <div className="mt-1">
            <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-500 transition-all duration-300"
                style={{ width: `${upload.progress}%` }}
              />
            </div>
          </div>
        )}
        {upload.error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400 leading-relaxed break-words">{upload.error}</p>
        )}
      </div>
      {isActive && (
        <button
          onClick={() => onCancel(upload.id)}
          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all"
          title="Cancel upload"
        >
          <X size={14} className="text-gray-500" />
        </button>
      )}
    </div>
  );
}

export default FilesSidebar;
